import type {EstablishedAhpSessionBootstrap, SubmissionRecovery} from '@github-ui/ahp-session-client/contracts'
import type {ManagedEstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import type {InstructionRuleSessionPublisher} from '@github-ui/copilot-chat/utils/instruction-rules-publisher'

export class TaskLaunchAttempt {
  readonly #taskId: string
  #disposed = false
  #unsubscribeRecovery?: () => void
  #bootstrap?: EstablishedAhpSessionBootstrap
  #submission?: ManagedEstablishedAhpInitialSubmissionCoordinator
  #instructionRulePublisher?: InstructionRuleSessionPublisher

  constructor(
    taskId: string,
    bootstrap?: EstablishedAhpSessionBootstrap,
    submission?: ManagedEstablishedAhpInitialSubmissionCoordinator,
  ) {
    this.#taskId = taskId
    this.#bootstrap = bootstrap
    this.#submission = submission
  }

  get disposed(): boolean {
    return this.#disposed
  }

  get bootstrap(): EstablishedAhpSessionBootstrap | undefined {
    return this.#bootstrap
  }

  get submission(): ManagedEstablishedAhpInitialSubmissionCoordinator | undefined {
    return this.#submission
  }

  attachBootstrap(bootstrap: EstablishedAhpSessionBootstrap): void {
    if (this.#disposed) {
      bootstrap.dispose()
      throw new Error('Cannot attach a bootstrap to a disposed startup attempt.')
    }
    this.#bootstrap = bootstrap
  }

  attachSubmission(submission: ManagedEstablishedAhpInitialSubmissionCoordinator): void {
    if (this.#disposed) {
      submission.dispose()
      throw new Error('Cannot attach a submission to a disposed startup attempt.')
    }
    this.#submission = submission
  }

  attachInstructionRulePublisher(publisher: InstructionRuleSessionPublisher): void {
    if (this.#disposed) {
      publisher.deactivate(this.#taskId)
      publisher.dispose()
      throw new Error('Cannot attach an instruction publisher to a disposed startup attempt.')
    }
    this.#instructionRulePublisher = publisher
  }

  observeRecovery(recovery: SubmissionRecovery, listener: () => void): void {
    if (this.#disposed || this.#unsubscribeRecovery) return
    this.#unsubscribeRecovery = recovery.subscribe(listener)
    listener()
  }

  takeBootstrap(): EstablishedAhpSessionBootstrap | undefined {
    const bootstrap = this.#bootstrap
    this.#bootstrap = undefined
    return bootstrap
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.#unsubscribeRecovery?.()
    this.#unsubscribeRecovery = undefined
    this.#submission?.dispose()
    this.#submission = undefined
    this.#instructionRulePublisher?.deactivate(this.#taskId)
    this.#instructionRulePublisher?.dispose()
    this.#instructionRulePublisher = undefined
    this.#bootstrap?.dispose()
    this.#bootstrap = undefined
  }
}
